/* eslint-disable @typescript-eslint/no-unused-expressions */
import { DBRow, IoHowTos, IoHowToSteps } from '@cloudio-saas/datasource-types';
import { atom, useEvent, useStateXAtomValue } from '@cloudio-saas/statex';
import { Box, SpeedDial, SpeedDialAction, SpeedDialIcon } from '@mui/material';
import axios from 'axios';
import {
  CaptureEventMap,
  CapturePubSubContext,
  useCapturePubSub,
} from 'common/Context';
import { Platform } from 'componentregistry';
import { Result } from 'components/AttachmentField/Attachment';
import { getBaseUrl, getUrl } from 'config/config';
import zIndex from 'constants/ZIndex';
import copy from 'copy-to-clipboard';
import { developerModeAtom } from 'designer/atoms';
import useConstant from 'hooks/useConstant';
import Icon from 'icons/Icon';
import { memo, ReactNode, useEffect, useRef, useState } from 'react';
import useUser, { UserSession } from 'root/useUser';
import { Descendant } from 'slate';
import Store from 'store/Store';
import useDataStore from 'store/useDataStore';
import { recordId } from 'store/utils/utils';
import { SyncPubSub } from 'utils/SyncPubSub';
import { getErrorMessage, isNotEmpty, isNull, shrink } from '../utils/isEmpty';
import { slugifyForURL } from '../utils/slugify';
import { getURLQueryStringToPost } from '../utils/url';
import useEventListener from '../utils/useEventListener';
import { getLabel } from './getLabel';

let video: HTMLVideoElement | undefined;
let counter = 0;
const IMAGE_WIDTH = 692;
const IMAGE_HEIGHT = 336;

const save = async (
  appUid: string,
  platform: Platform,
  howToStore: Store<IoHowTos>,
  stepsStore: Store<IoHowToSteps>,
  startTime: number,
  keyStrokes: string,
) => {
  const rec = howToStore.getCurrentRecord() as DBRow<IoHowTos>;
  if (!rec) {
    throw new Error('Missing how to record');
  }
  const addTo = stepsStore.data.addTo as AddTo | undefined;

  if (isNotEmpty(keyStrokes)) {
    let desc: Descendant[] | undefined;
    if (keyStrokes.length > 120) {
      desc = [
        {
          type: 'p',
          children: [{ text: keyStrokes }],
        },
      ];
      keyStrokes = ' the following...';
    }

    const stepNo = ++counter;
    const cId = await stepsStore.createNew({
      partialRecord: {
        stepTitle: shrink(`Type ${keyStrokes}`, 120),
        stepNo,
        appUid,
        howToUid: rec.howToUid,
        totalSeconds: Math.floor((Date.now() - startTime) / 1000),
        stepDescription: desc,
      },
      rs: 'I',
      addAfter: addTo?.insertAfterStepUid,
      addOnTop: addTo ? (addTo.insertAfterStepUid ? false : true) : false,
    });
    if (addTo && cId) {
      addTo.insertAfterStepUid = cId;
    }
  }

  if (addTo) {
    let stepNo = 0;
    stepsStore.recordsOrder().forEach((r) => {
      stepNo++;
      stepsStore.updateRecord(r, {
        stepNo,
      });
    });
  }

  const totalSeconds = Math.floor((Date.now() - startTime) / 1000);
  howToStore.updateRecord(recordId(rec), {
    totalSeconds: addTo ? rec.totalSeconds + totalSeconds : totalSeconds,
    totalSteps: stepsStore.size(),
  });
  await Promise.all([
    howToStore.save({ feedback: 'NONE' }),
    stepsStore.save({ feedback: 'NONE' }),
  ]);

  const url = `${getBaseUrl()}#/how-to/${appUid}/${rec.howToUid}`;
  copy(url, {
    debug: false,
    format: 'text/plain',
  });

  platform.showSuccess(`URL copied to clipboard: ${url}`);
  platform.setter(developerModeAtom, false, { params: { appUid } });
  window.location.href = `#/${appUid}/how-to/${rec.howToUid}`;

  // const lines: string[] = [];
  // const zip = JsZip();
  // const uid = ulid();
  // steps.forEach((step) => {
  //   lines.push(`### ${step.label}`);
  //   lines.push('');
  //   if (step.blob) {
  //     lines.push(`![](<../.gitbook/assets/${uid}/step_${step.seq}.png>)`);
  //     lines.push('');
  //     zip.file(`dot_gitbook/assets/${uid}/step_${step.seq}.png`, step.blob);
  //   }
  // });
  // const blob = new Blob([lines.join('\n')], {
  //   type: 'text/plain;charset=utf-8',
  // });
  // zip.file('how-to/steps.md', blob);
  // zip.generateAsync({ type: 'blob' }).then((zipFile) => {
  //   FileSaver.saveAs(zipFile, 'steps.zip');
  //   steps = [];
  // });
};

let stream: MediaStream | undefined;

const startCapture = async (
  appUid: string,
  platform: Platform,
  howToStore: Store<IoHowTos>,
  stepsStore: Store<IoHowToSteps>,
  addTo?: AddTo,
): Promise<boolean> => {
  if (!video) {
    counter = 0;
    const v = document.createElement('video');
    stream = await navigator.mediaDevices.getDisplayMedia({
      // @ts-ignore chrome only
      preferCurrentTab: true,
      audio: false,
    });
    v.srcObject = stream;
    await v.play();
    video = v;
  }

  if (addTo) {
    // no need to create a new how to
    return true;
  }
  await howToStore.clear();
  await stepsStore.clear();
  let uniqueName = false;
  let howToTitle = 'How To do something?';
  while (!uniqueName) {
    howToTitle = await platform.userInput({
      title: 'How To Title',
      label: 'Title',
      defaultName: howToTitle,
      content: 'Please enter a title for the How To (Max 60 characters)',
      required: true,
      isValid: (name) => !isNull(name) && name.length <= 60,
      feedback: (name) => `URI: ${slugifyForURL(name, 60)}`,
    });
    const records = await howToStore.query({
      props: {
        filter: [{ howToTitle: { is: howToTitle } }],
        limit: 1,
        projection: { howToTitle: 1 },
      },
    });
    if (!records || Object.values(records).length === 0) {
      uniqueName = true;
      break;
    }
    platform.showWarning(
      'How To title must be unique! An how to with this title already exists.',
    );
    await howToStore.clear();
  }
  let initUri = window.location.href;
  if (
    initUri.indexOf('#') > 0 &&
    !['howto', 'cloudio', 'howtos'].includes(appUid)
  ) {
    initUri = initUri.substring(initUri.indexOf('#'));
  } else {
    initUri = '#/';
  }
  await howToStore.createNew({
    partialRecord: {
      appUid,
      howToTitle,
      icon: 'desktop',
      howToUid: slugifyForURL(howToTitle, 60),
      totalSeconds: 0,
      totalViews: 0,
      totalSteps: 0,
      initUri,
    },
    rs: 'I',
  });
  return howToStore.save({ feedback: 'NONE' });
  // steps = [];
};

async function stopCapture(
  appUid: string,
  platform: Platform,
  howToStore: Store<IoHowTos>,
  stepsStore: Store<IoHowToSteps>,
  startTime: number,
  keyStrokes: string,
) {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
    stream = undefined;
    video = undefined;
  }
  if (stepsStore.isStoreDirty()) {
    await save(appUid, platform, howToStore, stepsStore, startTime, keyStrokes);
  } else {
    const { howToUid } = howToStore.getCurrentRecord() ?? {};
    if (howToUid) {
      window.location.href = `#/${appUid}/how-to/${howToUid}`;
    }
  }
}

function canCapture(e: MouseEvent): boolean {
  let el = e.target as HTMLElement | null;
  if (!el) return false;
  if (el.tagName === 'UL' || el.tagName === 'OL' || el.tagName === 'BODY') {
    return false;
  }
  if (el.classList.contains('MuiTabs-scroller')) {
    return false;
  }
  if (el instanceof HTMLInputElement && el.disabled) {
    return false;
  }
  if (el instanceof HTMLLabelElement && isNotEmpty(el.htmlFor)) {
    return false;
  }
  if (e.metaKey) return true;
  while (el) {
    if (el.getAttribute('data-nocapture') === 'true') {
      return false;
    }
    el = el.parentElement;
  }
  return true;
}

export const capture = ({
  e,
  user,
  appUid,
  platform,
  stepsStore,
  howToStore,
  startTime,
  keyStrokes,
  fullScreen = false,
}: {
  e: MouseEvent;
  user: UserSession;
  appUid: string;
  platform: Platform;
  stepsStore: Store<IoHowToSteps>;
  howToStore: Store<IoHowTos>;
  startTime: number;
  keyStrokes: string;
  fullScreen?: boolean;
}): Promise<boolean> => {
  let label = fullScreen
    ? 'Full Screen View'
    : getLabel(e.target as HTMLElement);
  if (e.button !== 0) {
    label = label.replace('Click', 'Right Click');
  }
  return captureInternal({
    clientX: e.clientX,
    clientY: e.clientY,
    label,
    user,
    appUid,
    platform,
    stepsStore,
    howToStore,
    startTime,
    keyStrokes,
    fullScreen,
  });
};
// do not use async here as we need to capture the screenshot synchronously
const captureInternal = ({
  clientX,
  clientY,
  label,
  user,
  appUid,
  platform,
  stepsStore,
  howToStore,
  startTime,
  keyStrokes,
  fullScreen = false,
}: {
  clientX: number;
  clientY: number;
  label: string;
  user: UserSession;
  appUid: string;
  platform: Platform;
  stepsStore: Store<IoHowToSteps>;
  howToStore: Store<IoHowTos>;
  startTime: number;
  keyStrokes: string;
  fullScreen?: boolean;
}): Promise<boolean> => {
  if (!video) {
    throw new Error('Missing video!');
  }

  const howToUid = howToStore.getCurrentRecord()?.howToUid;
  if (!howToUid) {
    return Promise.resolve(false);
  }
  let stepNo = ++counter;

  const addTo = stepsStore.data.addTo as AddTo | undefined;
  if (isNotEmpty(keyStrokes)) {
    let desc: Descendant[] | undefined;
    if (keyStrokes.length > 120) {
      desc = [
        {
          type: 'p',
          children: [{ text: keyStrokes }],
        },
      ];
      keyStrokes = ' the following...';
    }

    stepsStore
      .createNew({
        partialRecord: {
          stepTitle: shrink(`Type ${keyStrokes}`, 120),
          stepNo,
          appUid,
          howToUid,
          totalSeconds: Math.floor((Date.now() - startTime) / 1000),
          stepDescription: desc,
        },
        rs: 'I',
        addAfter: addTo?.insertAfterStepUid,
        addOnTop: addTo ? (addTo.insertAfterStepUid ? false : true) : false,
      })
      .then((cId) => {
        if (addTo && cId) {
          addTo.insertAfterStepUid = cId;
        }
      });

    startTime = Date.now();
    stepNo = ++counter;
  }

  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!video || !context) {
    throw new Error('Canvas context is not defined');
  }

  const vw = Math.min(video.videoWidth, window.innerWidth);
  const vh = Math.min(video.videoHeight, window.innerHeight);

  canvas.width = vw;
  canvas.height = vh;
  context.fillStyle = 'white';
  context.fillRect(0, 0, vw, vh);

  const imageWidth = fullScreen ? vw : Math.min(IMAGE_WIDTH, vw);
  const imageHeight = fullScreen ? vh : Math.min(IMAGE_HEIGHT, vh);
  let x = clientX - imageWidth / 2;
  let y = clientY - imageHeight / 2;
  if (x < imageWidth / 4) {
    x = 0;
  }
  if (y < imageHeight / 4) {
    y = 0;
  }

  let x2 = x + imageWidth;
  let y2 = y + imageHeight;
  if (x2 > vw - imageWidth / 4) {
    x2 = vw;
    x = x2 - imageWidth;
  }
  if (y2 > vh - imageHeight / 4) {
    y2 = vh;
    y = y2 - imageHeight;
  }
  context.imageSmoothingQuality = 'high';
  video.pause();

  context.drawImage(video, 0, 0, vw, vh);

  let finalCanvas = canvas;
  if (!fullScreen) {
    context.beginPath();
    context.globalAlpha = 0.6;
    context.strokeStyle = '#FF9800';
    context.lineWidth = 2;
    context.arc(clientX, clientY, 20, 0, 2 * Math.PI);
    context.stroke();

    context.beginPath();
    context.fillStyle = '#FF9800';
    context.globalAlpha = 0.1;
    context.arc(clientX, clientY, 18, 0, 2 * Math.PI);
    context.fill();

    const canvas2 = document.createElement('canvas');
    const context2 = canvas2.getContext('2d');

    if (!context2) {
      throw new Error('Canvas context is not defined');
    }
    canvas2.width = imageWidth;
    canvas2.height = imageHeight;
    context2.drawImage(canvas, x, y, x2, y2, 0, 0, x2, y2);
    finalCanvas = canvas2;
  }
  video.play();
  return new Promise((resolve, reject) => {
    finalCanvas.toBlob(
      (blob) => {
        // steps.push({ label, seq: ++counter, cx, cy, blob });

        if (!blob) return;
        const formData = new FormData();
        const file = new File([blob], `step_${stepNo}.png`);
        formData.append('file', file);
        formData.append('datasourceUid', 'IoHowToSteps');
        axios({
          url: `${getUrl()}upload/file_attachment?${getURLQueryStringToPost(
            user,
          )}`,
          method: 'POST',
          withCredentials: true,
          headers: {
            'X-Application': appUid,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          data: formData,
        })
          .then(async ({ data }) => {
            const _data = data as Result;
            if (_data.status === 'ERROR') {
              platform.showError(_data.message);
            } else {
              const { fileUid } = _data;
              const cId = await stepsStore.createNew({
                partialRecord: {
                  imageUid: fileUid,
                  stepTitle: shrink(label, 120),
                  stepNo,
                  appUid,
                  howToUid,
                  totalSeconds: Math.floor((Date.now() - startTime) / 1000),
                },
                rs: 'I',
                addAfter: addTo?.insertAfterStepUid,
                addOnTop: addTo
                  ? addTo.insertAfterStepUid
                    ? false
                    : true
                  : false,
              });
              if (addTo && cId) {
                addTo.insertAfterStepUid = cId;
              }
            }
            resolve(true);
          })
          .catch((_e) => {
            platform.showError(_e.response?.data?.message ?? _e.message);
            reject(_e);
          });
      },
      'image/png',
      1.0,
    );
  });
};

export interface AddTo {
  howToUid: string;
  insertAfterStepUid?: string;
}
export const captureScreenAtom = atom<{
  active: boolean;
  addTo?: AddTo;
  appUid: string;
}>({
  path: ['transient', 'captureScreen'],
  defaultValue: {
    active: false,
    appUid: '',
  },
});

function ScreenCaptureInner({ platform }: { platform: Platform }) {
  const user = useUser();
  const { addTo, appUid } = useStateXAtomValue(captureScreenAtom);
  const [started, setStarted] = useState(false);
  const [hide, _setHide] = useState(false);
  const [paused, setPause] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const startTimeRef = useRef(new Date().getTime());
  const stepStartTimeRef = useRef(new Date().getTime());
  const pausedTimeRef = useRef(0);
  const keyStrokesRef = useRef('');
  // do not change the appUid during the recording
  const howToStore = useDataStore<IoHowTos>({
    alias: `IoHowTosAlias-${appUid}${addTo ? '' : '-record'}`,
    ds: 'IoHowTos',
    appUid,
    pageId: '$$Sidebar$$',
  });
  const stepsStore = useDataStore<IoHowToSteps>({
    alias: `IoHowToStepsAlias-${appUid}${addTo ? '' : '-record'}`,
    ds: 'IoHowToSteps',
    appUid,
    pageId: '$$Sidebar$$',
  });

  useEffect(() => {
    if (addTo) {
      stepsStore.data.addTo = { ...addTo };
    } else {
      stepsStore.data.addTo = undefined;
    }
  }, [addTo, stepsStore]);

  const toggleHide = useEvent((_hide: boolean) => {
    // if (_hide) {
    //   document.body.classList.add('io-capture');
    // } else {
    //   document.body.classList.remove('io-capture');
    // }
    _setHide(_hide);
  });

  const onMouseDownCapture = useEvent((e: React.MouseEvent) => {
    if (!started || paused || e.metaKey) return;
    const el = ref.current;
    if (!el) return;
    let parent: HTMLElement | null = e.target as HTMLElement;
    while (parent) {
      if (parent === el) {
        return;
      }
      parent = parent.parentElement;
    }
    if (!canCapture(e.nativeEvent)) {
      return;
    }
    toggleHide(true);
  });

  // do not async here as we need to capture the screenshot synchronously
  const onClickCapture = useEvent((e: React.MouseEvent) => {
    if (!started || (paused && !e.metaKey)) return;

    if (!canCapture(e.nativeEvent)) {
      toggleHide(false);
      return;
    }
    capture({
      e: e.nativeEvent,
      user,
      appUid,
      platform,
      howToStore,
      stepsStore,
      startTime: stepStartTimeRef.current,
      keyStrokes: keyStrokesRef.current,
    }).then(() => {
      toggleHide(false);
      keyStrokesRef.current = '';
      stepStartTimeRef.current = Date.now();
    });
  });

  const onCaptureRequest = useEvent(
    ({
      clientX,
      clientY,
      label,
    }: {
      clientX: number;
      clientY: number;
      label: string;
    }) => {
      if (!started || paused) return;
      toggleHide(true);
      setTimeout(() => {
        captureInternal({
          clientX,
          clientY,
          label,
          user,
          appUid,
          platform,
          howToStore,
          stepsStore,
          startTime: stepStartTimeRef.current,
          keyStrokes: keyStrokesRef.current,
        }).then(() => {
          toggleHide(false);
          keyStrokesRef.current = '';
          stepStartTimeRef.current = Date.now();
        });
      }, 100);
    },
  );

  const onContextMenuCapture = useEvent((e: React.MouseEvent) => {
    if (!started || paused) return;

    if (!canCapture(e.nativeEvent)) {
      toggleHide(false);
      return;
    }

    capture({
      e: e.nativeEvent,
      user,
      appUid,
      platform,
      howToStore,
      stepsStore,
      startTime: stepStartTimeRef.current,
      keyStrokes: keyStrokesRef.current,
    }).then(() => {
      toggleHide(false);
      keyStrokesRef.current = '';
      stepStartTimeRef.current = Date.now();
    });
  });

  const pubSub = useCapturePubSub();
  useEffect(() => {
    const unsub_click = pubSub.sub('click', (e, { event }) => {
      onClickCapture(event);
    });
    const unsub_capture = pubSub.sub('capture', (e, props) => {
      onCaptureRequest(props);
    });
    const unsub_contextmenu = pubSub.sub('contextmenu', (e, { event }) => {
      onContextMenuCapture(event);
    });
    const unsub_mousedown = pubSub.sub('mousedown', (e, { event }) => {
      onMouseDownCapture(event);
    });
    return () => {
      unsub_click();
      unsub_capture();
      unsub_contextmenu();
      unsub_mousedown();
    };
  }, [pubSub]);

  useEventListener(
    'keydown',
    async (e) => {
      if (!started || paused) return;
      if (e.metaKey && e.key === 'F2') {
        // console.log('meta+f2');
        e.stopPropagation();
        e.preventDefault();
        window.addEventListener(
          'mousemove',
          async (event) => {
            // console.log('mousemove', event);
            await capture({
              e: event,
              user,
              appUid,
              platform,
              howToStore,
              stepsStore,
              startTime: stepStartTimeRef.current,
              keyStrokes: keyStrokesRef.current,
            });
            keyStrokesRef.current = '';
            stepStartTimeRef.current = Date.now();
          },
          { once: true },
        );
      }
      if (
        [
          'Alt',
          'ArrowDown',
          'ArrowLeft',
          'ArrowRight',
          'ArrowUp',
          'Backspace',
          'CapsLock',
          'Control',
          'Delete',
          'End',
          'Enter',
          'Escape',
          'F1',
          'F2',
          'F3',
          'F4',
          'F5',
          'F6',
          'F7',
          'F8',
          'F9',
          'F10',
          'F11',
          'F12',
          'Home',
          'Insert',
          'Meta',
          'PageDown',
          'PageUp',
          'Shift',
          'Tab',
        ].includes(e.key)
      ) {
        keyStrokesRef.current += ` <${e.key}> `;
      } else {
        keyStrokesRef.current += e.key;
      }
    },
    document,
    true,
  );

  useEffect(() => {
    startTimeRef.current = Date.now();
    stepStartTimeRef.current = Date.now();
    startCapture(appUid, platform, howToStore, stepsStore, addTo)
      .then((result) => {
        if (!result) {
          platform.setter(captureScreenAtom, captureScreenAtom.defaultValue);
        } else {
          setStarted(true);
        }
      })
      .catch((e) => {
        platform.showError(getErrorMessage(e));
        platform.setter(captureScreenAtom, captureScreenAtom.defaultValue);
      });
    return () => {
      stopCapture(
        appUid,
        platform,
        howToStore,
        stepsStore,
        startTimeRef.current,
        keyStrokesRef.current,
      ).catch((e) => {
        platform.showError(getErrorMessage(e));
      });
    };
  }, [addTo, appUid, howToStore, platform, stepsStore]);
  const [open, setOpen] = useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  if (!started || hide) return null;

  return (
    <SpeedDial
      ref={ref}
      className="io-capture-hide"
      data-nocapture
      ariaLabel="Screen Capture Controls"
      sx={{
        position: 'absolute',
        bottom: 16,
        right: 16,
        '& > button, & > button:hover': {
          bgcolor: paused ? 'warning.main' : 'error.main',
        },
        zIndex: zIndex.Model + 1,
      }}
      icon={<SpeedDialIcon />}
      onClose={handleClose}
      onOpen={handleOpen}
      open={open}
    >
      <SpeedDialAction
        open={open}
        onMouseDownCapture={async (e) => {
          handleClose();
          if (e.metaKey) {
            await capture({
              e: e.nativeEvent,
              user,
              appUid,
              platform,
              howToStore,
              stepsStore,
              startTime: stepStartTimeRef.current,
              keyStrokes: keyStrokesRef.current,
            });
            keyStrokesRef.current = '';
            stepStartTimeRef.current = Date.now();
          } else {
            platform.setter(captureScreenAtom, captureScreenAtom.defaultValue);
          }
        }}
        icon={
          <Icon
            iconName="stop-circle"
            iconType="duotone"
            color="error"
            width={40}
            height={40}
            sx={{ borderRadius: '50%' }}
          />
        }
        data-tip={'Stop Screen Capture'}
        data-tip-at="left"
        data-tip-delay={0}
      />
      <SpeedDialAction
        open={open}
        onMouseDownCapture={(e) => {
          handleClose();
          toggleHide(true);
          setTimeout(async () => {
            await capture({
              e: e.nativeEvent,
              user,
              appUid,
              platform,
              howToStore,
              stepsStore,
              startTime: stepStartTimeRef.current,
              keyStrokes: keyStrokesRef.current,
              fullScreen: true,
            });
            toggleHide(false);
            keyStrokesRef.current = '';
            stepStartTimeRef.current = Date.now();
          }, 100);
        }}
        icon={
          <Icon iconName="desktop" width={18} height={18} color="primary" />
        }
      />
      <SpeedDialAction
        open={open}
        onMouseDownCapture={(e) => {
          handleClose();
          if (!paused) {
            pausedTimeRef.current = Date.now();
            setPause(true);
          } else {
            stepStartTimeRef.current += Date.now() - pausedTimeRef.current;
            setTimeout(async () => {
              setPause(false);
            }, 1000);
          }
        }}
        icon={
          <Icon
            iconName={paused ? 'play' : 'pause'}
            width={18}
            height={18}
            color="primary"
          />
        }
        data-tip={`${paused ? 'Resume' : 'Pause'} Capture`}
        data-tip-at="left"
        data-tip-delay={0}
      />
    </SpeedDial>
  );
}

function ScreenCapture({ platform }: { platform: Platform }) {
  const captureScreen = useStateXAtomValue(captureScreenAtom);

  // const [ww, wh] = useWindowSize();
  // useEventListener(
  //   'click',
  //   async (e) => {
  //     if (!canCapture(e)) {
  //       return;
  //     }
  //     const label = getLabel(e.target as HTMLElement);
  //     platform.showInfo(
  //       `${e.clientX}x${e.clientY} ${label} ${ww}x${wh} ${window.innerHeight}`,
  //     );
  //   },
  //   document,
  //   true,
  // );
  if (!captureScreen.active) return null;

  return <ScreenCaptureInner platform={platform} />;
}

function ScreenCaptureWrapper({
  platform,
  children,
}: {
  platform: Platform;
  children: ReactNode;
}) {
  const pubSub = useConstant(() => new SyncPubSub<CaptureEventMap>());

  return (
    <CapturePubSubContext.Provider value={pubSub}>
      <Box
        className="io-capture-wrapper"
        sx={{ width: '100%', height: '100%' }}
        onMouseDownCapture={(event) => {
          pubSub.pub('mousedown', { event });
        }}
        onClickCapture={(event) => {
          pubSub.pub('click', { event });
        }}
        onContextMenuCapture={(event) => {
          pubSub.pub('contextmenu', { event });
        }}
      >
        {children}
        <ScreenCapture platform={platform} />
      </Box>
    </CapturePubSubContext.Provider>
  );
}

export default memo(ScreenCaptureWrapper);
